import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import {
  METRIC_TYPE,
  buildDrillDownHref,
  formatComparisonLine,
  formatKpiValue,
  pick,
} from "./managementDashboardUtils";

const CARD_SX = { borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" };

const ExecutiveKpiCard = ({ kpi, currencyUnit, loading, error, onRetry }) => {
  if (loading) {
    return (
      <Card sx={CARD_SX}>
        <CardContent>
          <Skeleton height={24} width="70%" />
          <Skeleton height={36} width="50%" sx={{ mt: 1 }} />
          <Skeleton height={18} width="40%" sx={{ mt: 1 }} />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={CARD_SX}>
        <CardContent>
          <Typography fontWeight={700} fontSize={14}>
            {pick(kpi, "title", "Title") || "شاخص"}
          </Typography>
          <Typography color="error" fontSize={13} sx={{ mt: 1 }}>
            {error}
          </Typography>
          {onRetry ? (
            <Typography
              component="button"
              onClick={onRetry}
              sx={{
                mt: 1,
                border: 0,
                bgcolor: "transparent",
                color: "primary.main",
                cursor: "pointer",
                p: 0,
              }}
            >
              تلاش مجدد
            </Typography>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  const title = pick(kpi, "title", "Title") || "—";
  const isAvailable = pick(kpi, "isAvailable", "IsAvailable");
  const unavailableReason = pick(kpi, "unavailableReason", "UnavailableReason");
  const metricType = pick(kpi, "metricType", "MetricType");
  const valueText = isAvailable === false ? null : formatKpiValue(kpi, currencyUnit);
  const drillHref = buildDrillDownHref(pick(kpi, "drillDown", "DrillDown"));

  return (
    <Card sx={{ ...CARD_SX, height: "100%" }}>
      <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <Box>
            <Typography fontWeight={700} fontSize={14}>
              {title}
            </Typography>
            <Chip
              size="small"
              label={
                metricType === METRIC_TYPE.Event ? "رویدادی" : "لحظه‌ای"
              }
              sx={{ mt: 0.5, height: 22, fontSize: 11 }}
              color={metricType === METRIC_TYPE.Event ? "info" : "default"}
              variant="outlined"
            />
          </Box>
          {drillHref ? (
            <Tooltip title="مشاهده جزئیات">
              <IconButton
                component={RouterLink}
                to={drillHref}
                size="small"
                aria-label="drill-down"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>

        {isAvailable === false ? (
          <Typography fontSize={13} color="text.secondary" sx={{ mt: 1.5 }}>
            {unavailableReason || "این شاخص در حال حاضر در دسترس نیست."}
          </Typography>
        ) : (
          <>
            <Typography fontWeight={800} fontSize={22} sx={{ mt: 1.5 }}>
              {valueText}
            </Typography>
            <Typography fontSize={12} color="text.secondary" sx={{ mt: 0.5 }}>
              {formatComparisonLine(kpi)}
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ExecutiveKpiCard;
